import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";
import { Tooltip } from "react-tooltip";

import { Page, Site } from "~/api/Api";
import { Icon } from "~/assets/icons/Icon";
import DataTable, {
  DataTableColumnMeta,
} from "~/components/ui/dataTable/DataTable";
import {
  Pagination,
  PaginationConfig,
} from "~/components/ui/pagination/Pagination";
import Spinner from "~/components/ui/spinner/Spinner";
import {
  MetricBand,
  bandFromValue,
} from "~/components/visibilityPerformance/bands";
import { isEngineHealthy } from "~/components/visibilityPerformance/engineHealth";
import { urlWithHighlight } from "~/helpers/siteHelper";
import { PagesResponseWithAuth } from "~/pages/dashboard/PagesPage";

import AchievedMetric from "./AchievedMetric";
import GainSparkline from "./GainSparkline";
import ScoreDonut, { COMPACT_DONUT_PROPS } from "./ScoreDonut";
import styles from "./VisibilityMetricsTable.module.scss";

interface Props {
  site: Site | null;
  data?: PagesResponseWithAuth;
  pagination: PaginationConfig;
  setPagination: (pagination: PaginationConfig) => void;
  isLoading: boolean;
  isFetching?: boolean;
}

const MAX_URL_CHARS = 60;

function truncateUrlStart(text: string): string {
  if (text.length <= MAX_URL_CHARS) return text;

  return `\u2026${text.slice(-(MAX_URL_CHARS - 1))}`;
}

// Penalize the visibility score for slow page loads. Pages that take longer
// than 2s to load lose up to half their score the slower they get.
function applyLoadPenalty(score: number, loadMs: number | undefined): number {
  if (loadMs == null) return score;
  const loadSec = loadMs / 1000;
  if (loadSec > 2) {
    const loadPenalty = (loadSec - 2) / loadSec / 2;

    return Math.round(score * (1 - loadPenalty));
  }

  return score;
}

type VisibilityTier =
  | "fully"
  | "mostly"
  | "partially"
  | "half"
  | "barely"
  | "invisible";

function getVisibilityTier(textRatio: number): VisibilityTier {
  const pct = Math.min(100, textRatio * 100);
  if (pct >= 90) return "fully";
  if (pct >= 80) return "mostly";
  if (pct >= 65) return "partially";
  if (pct >= 50) return "half";
  if (pct >= 25) return "barely";

  return "invisible";
}

// Keep the score colour consistent with the Segment tier (rather than the
// generic bandFromValue thresholds) so a "Partially Visible" page never reads
// as green, etc.
function bandFromTier(tier: VisibilityTier): MetricBand {
  if (tier === "fully" || tier === "mostly") return "good";
  if (tier === "partially" || tier === "half") return "warn";

  return "bad";
}

// Single source of truth for a page's visibility score so the Visibility Score
// donut/sparkline and the Segment badge never disagree at a tier boundary
// (the score is rounded once, then both the tier and band derive from it).
function computeVisibilityScore(
  report: NonNullable<Page["visibility_report"]>,
  performanceReports: Page["performance_reports"],
): { score: number; tier: VisibilityTier; band: MetricBand } {
  const perf =
    performanceReports?.find((r) => r.platform === "desktop") ?? null;
  const baseScore = Math.min(100, report.text_ratio * 100);
  const score = Math.round(applyLoadPenalty(baseScore, perf?.page_load));
  const tier = getVisibilityTier(score / 100);

  return { score, tier, band: bandFromTier(tier) };
}

const VISIBILITY_TIER_LABEL: Record<VisibilityTier, string> = {
  fully: "✅ Fully Visible",
  mostly: "🟢 Mostly Visible",
  partially: "🟡 Partially Visible",
  half: "🟠 Half Visible",
  barely: "🔴 Barely Visible",
  invisible: "❌ Invisible",
};

// Below this many points of improvement we show a single value instead of the
// before/after treatment — keeps near-perfect rows clean and consistent.
const MIN_GAIN = 5;

const SEG_CLASS: Record<VisibilityTier, string> = {
  fully: styles.segFully,
  mostly: styles.segMostly,
  partially: styles.segPartially,
  half: styles.segHalf,
  barely: styles.segBarely,
  invisible: styles.segInvisible,
};

const COLUMN_TOOLTIPS: Record<string, string> = {
  visibility_score:
    "Percentage of page text visible to AI crawlers. Derived from the raw HTML vs. rendered DOM comparison. Higher is better.",
  segment: "Tier of visibility to AI crawlers.",
  content_overlap:
    "Percentage of rendered page content that is also present in the raw HTML. A high overlap means bots see roughly the same content as users.",
};

// Placeholder cell shown while a page's AI-crawler report is still being
// generated (no visibility_report yet).
const ReportPendingCell = () => (
  <span
    className={styles.spinnerWrap}
    data-tooltip-id="vm-header-tooltip"
    data-tooltip-content="AI Crawler Metrics Report Pending"
  >
    <Spinner size="1.25rem" />
  </span>
);

// Metric columns share the same center-aligned, auto-width treatment.
const metricMeta = {
  align: "center",
  autoWidth: true,
} satisfies DataTableColumnMeta;

// Column header with the dashed-underline hint and a shared tooltip.
const TooltipHeader = ({ id, label }: { id: string; label: string }) => (
  <span
    className={styles.thWithTooltip}
    data-tooltip-id="vm-header-tooltip"
    data-tooltip-content={COLUMN_TOOLTIPS[id]}
  >
    {label}
  </span>
);

const VisibilityMetricsTable = ({
  site,
  data,
  pagination,
  setPagination,
  isLoading,
  isFetching,
}: Props) => {
  const pages = data?.pages ?? [];
  const engineHealthy = isEngineHealthy(site);

  const columns = useMemo<ColumnDef<Page>[]>(
    () => [
      {
        accessorKey: "url",
        header: "Page",
        enableSorting: false,
        meta: { autoWidth: true } satisfies DataTableColumnMeta,
        cell: ({ row }) => {
          const href = urlWithHighlight(row.original.url);

          return (
            <a
              target="_blank"
              rel="noreferrer"
              href={href}
              className={styles.pageLink}
            >
              {truncateUrlStart(row.original.path_and_query)}
            </a>
          );
        },
      },
      {
        id: "visibility_score",
        header: () => (
          <TooltipHeader id="visibility_score" label="Visibility Score" />
        ),
        enableSorting: false,
        meta: metricMeta,
        cell: ({ row }) => {
          const report = row.original.visibility_report;
          if (!report) return <ReportPendingCell />;

          const { score, band } = computeVisibilityScore(
            report,
            row.original.performance_reports,
          );
          const gain = 100 - score;
          if (!engineHealthy || gain < MIN_GAIN) {
            return (
              <ScoreDonut
                value={score}
                band={band}
                suffix=""
                {...COMPACT_DONUT_PROPS}
                tooltip={
                  score < 100
                    ? `After installing Visibility Engine, this score will increase from ${score} to 100.`
                    : undefined
                }
              />
            );
          }

          return (
            <GainSparkline
              before={score}
              band={band}
              label="Visibility Score"
            />
          );
        },
      },
      {
        id: "segment",
        header: () => <TooltipHeader id="segment" label="Segment" />,
        enableSorting: false,
        meta: metricMeta,
        cell: ({ row }) => {
          const report = row.original.visibility_report;
          if (!report) return <ReportPendingCell />;

          const { tier } = computeVisibilityScore(
            report,
            row.original.performance_reports,
          );
          const beforeEl = (
            <span
              className={SEG_CLASS[tier]}
              style={tier !== "fully" ? { cursor: "help" } : undefined}
              data-tooltip-id={
                tier !== "fully" ? "vm-header-tooltip" : undefined
              }
              data-tooltip-content={
                tier !== "fully"
                  ? "After installing Visibility Engine, this page will be Fully Visible."
                  : undefined
              }
            >
              {VISIBILITY_TIER_LABEL[tier]}
            </span>
          );
          if (!engineHealthy || tier === "fully") return beforeEl;

          return (
            <span className={styles.segTransition}>
              <span className={SEG_CLASS[tier]}>
                {VISIBILITY_TIER_LABEL[tier].replace(" Visible", "")}
              </span>
              <Icon iconName="PercentUpIcon" width="1rem" height="1rem" />
              <span className={SEG_CLASS.fully}>
                {VISIBILITY_TIER_LABEL.fully}
              </span>
            </span>
          );
        },
      },
      {
        id: "content_overlap",
        header: () => (
          <TooltipHeader id="content_overlap" label="Content Overlap" />
        ),
        enableSorting: false,
        meta: metricMeta,
        cell: ({ row }) => {
          const report = row.original.visibility_report;
          if (!report) return <ReportPendingCell />;

          const before = Math.min(
            100,
            Math.round(report.content_overlap * 100),
          );
          const gain = 100 - before;
          const band = bandFromValue(before / 100);
          if (!engineHealthy || gain < MIN_GAIN)
            return (
              <ScoreDonut
                value={before}
                band={band}
                suffix=""
                {...COMPACT_DONUT_PROPS}
                tooltip={
                  before < 100
                    ? `After installing Visibility Engine, this score will increase from ${before} to 100.`
                    : undefined
                }
              />
            );

          return (
            <AchievedMetric
              gain={gain}
              tooltip={`Your Content Overlap was ${before}% and is now 100%, a ${gain}% improvement.`}
            />
          );
        },
      },
    ],
    [engineHealthy],
  );

  const handlePageChange = (page: number) =>
    setPagination({ ...pagination, currentPage: page });
  const handleItemsPerPageChange = (items: number) =>
    setPagination({ ...pagination, itemsPerPage: items, currentPage: 1 });

  return (
    <>
      <DataTable
        columns={columns}
        data={pages}
        isLoading={isLoading}
        skeletonRows={pagination.itemsPerPage}
        emptyText="No pages found."
        tableLayout="auto"
      />
      <Pagination
        {...pagination}
        isLoading={isFetching ?? isLoading}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
      />
      <Tooltip
        id="vm-header-tooltip"
        style={{
          borderRadius: "var(--border-radius)",
          fontSize: "0.75rem",
          maxWidth: "280px",
        }}
      />
    </>
  );
};

export default VisibilityMetricsTable;
